import React, { createContext, useEffect, useMemo, useState } from 'react';
import { matchPath } from 'react-router-dom';

import { useContextSafe } from 'helpers/utils';
import usePublicApi from 'public-api';
import { useAsyncFunction } from 'stores/hooks';

type TPublicCompanyContext = {
  company: BackendPublic.PublicCompany | null;
  handleLoadCompany: () => Promise<void>;
  isLoadingCompany: boolean;
};

const PublicCompanyContext = createContext<TPublicCompanyContext | null>(null);

function PublicCompanyProvider({ children }: { children: React.ReactNode }) {
  // Matches base path of each client route
  const { companyId } = matchPath({ path: '/:lang/client/:companyId/', end: false }, window.location.pathname)
    ?.params as { companyId: string };

  const publicApi = usePublicApi();

  const [company, setCompany] = useState<BackendPublic.PublicCompany | null>(null);

  const [handleLoadCompany, isLoadingCompany] = useAsyncFunction(async () => {
    const result = await publicApi.CompanyCompanyApi.retrieve({ id: companyId });
    setCompany(result);
  });

  useEffect(() => {
    handleLoadCompany();
  }, [companyId]);

  const contextValue = useMemo(
    () => ({
      company,
      handleLoadCompany,
      isLoadingCompany,
    }),
    [company, handleLoadCompany, isLoadingCompany],
  );

  return <PublicCompanyContext.Provider value={contextValue}>{children}</PublicCompanyContext.Provider>;
}

const usePublicCompany = () => useContextSafe(PublicCompanyContext);

export { PublicCompanyContext, PublicCompanyProvider, usePublicCompany };
